import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, ConfirmModal, Icon } from '../../../components';
import { Modal } from '../../../components/Modal';
import { ManagementPagination } from '../../../components/ManagementFilters';
import { pageCardClasses } from '../../../components/AppLayout/styles';
import { strings, sendAllMessageStrings, commonStrings } from '../../../constants';
import { getDraftMessages, deleteDraftMessage } from '../../../api/messages';
import { getAutoReplyMessageAssets } from '../../../api';
import { DraftMessage } from '../../../types';
import useToast from '../../../hooks/useToast';
import { routes } from '../../../constants/routes';
import { useOrgPath } from '../../../hooks/useOrgPath';
import MessagePreview from '../MessagePreview';
import { convertDraftSectionsToMessageSections } from '../utils';
import { DraftsTable } from '../Drafts/DraftsTable';
import { DraftsCardList } from '../Drafts/DraftsCardList';
import { queryKeys } from '../../../constants/queryKeys';
const PAGE_SIZE = 10;

function DraftsList(): JSX.Element {
  const navigate = useNavigate();
  const orgPathFor = useOrgPath();
  const { toastError, toastSuccess } = useToast();
  const [drafts, setDrafts] = useState<DraftMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteDraftId, setDeleteDraftId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [previewDraftId, setPreviewDraftId] = useState<string | null>(null);
  const { data: assets = [] } = useQuery({
    queryKey: [queryKeys.AUTO_REPLY_MESSAGE_ASSETS],
    queryFn: () => getAutoReplyMessageAssets(),
  });

  const countLabel = useMemo(() => `${total}${commonStrings.countSuffix}`, [total]);
  const loadDrafts = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getDraftMessages({
        currentPage,
        pageSize: PAGE_SIZE,
      });
      setDrafts(response.data);
      setTotal(response.total);
      setCurrentPage(response.currentPage);
      setTotalPage(response.totalPage);
    } catch (error) {
      toastError(error);
    } finally {
      setIsLoading(false);
    }
  }, [toastError, currentPage]);

  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  const handleDeleteDraft = useCallback(async () => {
    if (!deleteDraftId) return;
    try {
      await deleteDraftMessage(deleteDraftId);
      toastSuccess(sendAllMessageStrings.drafts.messages.deleted);
      setDeleteDraftId(null);
      loadDrafts();
    } catch (error) {
      toastError(error);
    }
  }, [deleteDraftId,
    toastError,
    toastSuccess,
    loadDrafts
  ]);

  // プレビュー内容は一覧から導出する。本文を別stateへ複製すると、削除や
  // 再取得で一覧が変わってもスナップショットが古いまま残る
  const previewDraft = useMemo(
    () => drafts.find((draft) => draft.id === previewDraftId) ?? null,
    [drafts, previewDraftId],
  );

  const closePreview = useCallback(() => {
    setPreviewDraftId(null);
  }, []);

  const handlePreviewDraft = useCallback((draft: DraftMessage) => {
    setPreviewDraftId((currentId) =>
      currentId === draft.id ? null : draft.id,
    );
  }, []);


  return (
    <>
      <section className={`w-full ${pageCardClasses.regular}`}>
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="grid gap-2">
            <h1 className="text-2xl font-display text-ink">
              {sendAllMessageStrings.drafts.listTitle}
            </h1>
          </div>
          <div className="flex w-full flex-wrap items-center justify-between gap-3 sm:w-auto sm:justify-end">
            <span className="rounded-full border border-border/70 bg-white px-3 py-1 text-xs font-semibold text-muted">
              {countLabel}
            </span>
            <ManagementPagination
              currentPage={currentPage}
              totalPage={totalPage}
              loading={isLoading}
              onPrevious={() =>
                setCurrentPage((prev) => Math.max(1, prev - 1))
              }
              onNext={() => setCurrentPage((prev) => prev + 1)}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => navigate(orgPathFor(routes.SEND_MESSAGE_FOR_ALL))}
            >
              {sendAllMessageStrings.backToSendAll}
            </Button>
          </div>
        </header>

        <div className="relative">
          <div className="mt-6 hidden overflow-x-auto rounded-2xl border border-border/70 bg-white table-scroll lg:block">
            <DraftsTable
              drafts={drafts}
              assets={assets}
              loading={isLoading}
              onApply={(draftId) => navigate(orgPathFor(`${routes.SEND_MESSAGE_FOR_ALL}?draftId=${draftId}`))}
              onDelete={(draftId) => setDeleteDraftId(draftId)}
              onPreview={handlePreviewDraft}
            />
          </div>
          <div className="mt-6 lg:hidden">
            <DraftsCardList
              drafts={drafts}
              assets={assets}
              loading={isLoading}
              onApply={(draftId) => navigate(orgPathFor(`${routes.SEND_MESSAGE_FOR_ALL}?draftId=${draftId}`))}
              onDelete={(draftId) => setDeleteDraftId(draftId)}
              onPreview={handlePreviewDraft}
              className="md:grid-cols-2"
            />
          </div>
        </div>

        <Modal open={previewDraft !== null} onClose={closePreview}>
          <div className="grid gap-4">
            <div className="flex items-start justify-between border-b border-neutral-200 pb-4">
              <h2 className="text-base font-bold text-neutral-900">
                {sendAllMessageStrings.drafts.actions.preview}
              </h2>
              <button
                type="button"
                className="rounded-lg p-1 text-neutral-500 hover:bg-neutral-100"
                aria-label={commonStrings.close}
                onClick={closePreview}
              >
                <Icon icon="close" width={20} height={20} />
              </button>
            </div>
            <div className="flex items-center justify-center">
              <div className="relative w-full max-w-[320px] h-[min(600px,70dvh)] rounded-[3rem] border-8 border-neutral-50 flex flex-col overflow-hidden">
                <MessagePreview
                  sections={convertDraftSectionsToMessageSections(
                    previewDraft?.sections ?? [],
                  )}
                  assets={assets}
                  enableNameInsertionPreview
                />
              </div>
            </div>
          </div>
        </Modal>

        <ConfirmModal
          textTitle={sendAllMessageStrings.drafts.deleteConfirm.title}
          textMessage={sendAllMessageStrings.drafts.deleteConfirm.message}
          textCancel={strings.common.cancel}
          textConfirm={sendAllMessageStrings.drafts.actions.delete}
          onClose={() => setDeleteDraftId(null)}
          onConfirm={handleDeleteDraft}
          open={!!deleteDraftId}
        />
      </section>
    </>
  );
}

export default DraftsList;
